/**
 * ActeNouveauPage — rédiger un acte À PARTIR D'UNE FICHE.
 *
 * ─── 🔴 CETTE PAGE COMBLE DES LIENS MORTS ───────────────────────────────────
 *
 * Deux écrans pointaient déjà vers `/actes/nouveau` — l'agenda GRC (« Générer
 * un arrêté » sur un événement) et les réservations de matériel — alors
 * qu'AUCUNE route de ce nom n'existait. Les deux boutons menaient à une page
 * blanche.
 *
 * D4 avait besoin du même chemin pour les délibérations. Plutôt que d'inventer
 * une troisième variante, on écrit la page qui manquait : elle sert les trois.
 *
 * ─── CE QU'ELLE FAIT, ET POURQUOI C'EST LE POINT DE D4 ──────────────────────
 *
 * Elle passe la SOURCE au moteur de variables. Pour une délibération, cela
 * remplit `{{numero_deliberation}}` (32 modèles) et `{{date_deliberation}}`
 * (36 modèles) — 68 occurrences jusqu'ici tapées à la main.
 *
 * Ce n'est pas du confort : un arrêté qui vise la mauvaise délibération est
 * attaquable. Ce que la mairie sait déjà, elle ne doit pas avoir à le retaper.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppHeader } from '@/components/AppHeader';
import { Card, Alert, Button, ModuleHeader } from '@/components/ui';
import { ChampsModele, type ChampModele } from '@/components/ChampsModele';
import { api, ApiException } from '@/lib/api';
import { Stamp, Loader2, AlertCircle, Info, FileCheck2 } from 'lucide-react';

interface Modele {
  id: string; libelle: string;
  /** Texte libre côté commune : sert au filtre, jamais à du droit. */
  categorie: string | null;
  /** « individuel » (se notifie) vs « réglementaire » (s'affiche). */
  typeActe: string | null;
  code: string;
}

const CHAMP =
  'h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink';
const LABEL = 'mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle';

/** Ce que la source raconte à l'écran, pour que le rédacteur sache ce qu'il vise. */
const SOURCES: Record<string, string> = {
  deliberation: 'une délibération du conseil',
  evenement: 'un événement de l\u2019agenda',
  reservation: 'une réservation de matériel',
  agent: 'un dossier agent',
  signalement: 'un signalement',
  absence: 'une absence',
};

export default function ActeNouveauPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sourceType = params.get('sourceType');
  const sourceId = params.get('sourceId');
  // D5-01 — une suite proposée depuis une délibération porte SON modèle. Le
  // rédacteur arrive sur le bon canevas, il ne le cherche pas dans 150 entrées.
  const modeleDemande = params.get('modeleId');

  const [modeles, setModeles] = useState<Modele[]>([]);
  const [modeleId, setModeleId] = useState('');
  // Filtres du catalogue (29/07). Le catalogue compte ~150 modèles : sans eux,
  // choisir revient à parcourir la liste entière à chaque acte.
  const [filtreCategorie, setFiltreCategorie] = useState('');
  const [filtreType, setFiltreType] = useState('');
  const [recherche, setRecherche] = useState('');
  const [titre, setTitre] = useState('');
  const [objet, setObjet] = useState('');
  const [corps, setCorps] = useState('');
  const [champs, setChamps] = useState<ChampModele[]>([]);
  const [valeurs, setValeurs] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const liste = await api.acteModeles.list();
      // On garde catégorie, type et code : ce sont eux qui rendent le catalogue
      // navigable. Les jeter, c'était condamner l'utilisateur à dérouler ~150
      // entrées — remonté par Henrico le 29/07 en arrivant d'une délibération.
      setModeles(liste.map((m) => ({
        id: m.id, libelle: m.libelle, code: m.code,
        categorie: m.categorie ?? null, typeActe: m.typeActe ?? null,
      })));
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Catalogue illisible');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  /**
   * Le modèle demandé par l'URL est préparé dès que le catalogue est là.
   *
   * On attend `modeles` : le préparer avant que la liste soit chargée
   * afficherait un `<select>` vide alors que le corps est rempli — l'écran
   * dirait le contraire de ce qu'il fait.
   */
  useEffect(() => {
    if (modeleDemande && modeles.length > 0 && !modeleId) {
      void choisirModele(modeleDemande);
    }
    // `choisirModele` est stable dans cette page (pas de dépendance mouvante).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeleDemande, modeles, modeleId]);

  /** Les catégories réellement présentes — jamais une liste en dur. */
  const categories = Array.from(
    new Set(modeles.map((m) => m.categorie).filter((c): c is string => !!c)),
  ).sort((a, b) => a.localeCompare(b, 'fr'));

  /**
   * Le catalogue filtré.
   *
   * La recherche porte sur le libellé ET le code : une commune qui connaît
   * « RH-REC-001 » doit pouvoir le taper. Comparaison sans accents ni casse,
   * sinon « arrêté » ne trouve pas « ARRETE ».
   */
  const sansAccent = (v: string) =>
    v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const q = sansAccent(recherche.trim());
  const modelesFiltres = modeles.filter((m) => {
    if (filtreCategorie && m.categorie !== filtreCategorie) return false;
    if (filtreType && m.typeActe !== filtreType) return false;
    if (!q) return true;
    return sansAccent(`${m.libelle} ${m.code}`).includes(q);
  });

  async function choisirModele(id: string) {
    setModeleId(id);
    if (!id) { setChamps([]); setCorps(''); return; }
    setError(null);
    try {
      // C'EST ICI que D4 se joue : la source part avec la demande, et le
      // serveur remplit ce qu'il sait déjà.
      const r = await api.acteModeles.preparer(id,
        sourceType && sourceId ? { sourceType, sourceId } : undefined);
      setCorps(r.corps);
      setChamps(r.champs);
      setValeurs({});
      // Le titre suit le modèle tant que le rédacteur ne l'a pas réécrit.
      setTitre((actuel) => (actuel.trim() ? actuel : r.modele.libelle));
      setObjet((actuel) => (actuel.trim() ? actuel : r.modele.libelle));
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Modèle illisible');
    }
  }

  const resteASaisir = useMemo(
    () => champs.filter((c) => !valeurs[c.cle]?.trim()).length, [champs, valeurs]);

  async function creer() {
    if (!titre.trim()) { setError('Un titre est attendu'); return; }
    setSaving(true); setError(null);
    try {
      // Les champs saisis remplacent leurs accolades dans le corps. Ce qui
      // reste garde les siennes : un trou visible vaut mieux qu'un blanc
      // silencieux dans un acte signé.
      let texte = corps;
      for (const [cle, val] of Object.entries(valeurs)) {
        if (val.trim()) texte = texte.split(`{{${cle}}}`).join(val.trim());
      }
      const acte = await api.actes.creer({
        titre: titre.trim(),
        objet: objet.trim() || null,
        modeleId: modeleId || null,
        corps: texte,
        sourceType: sourceType ?? null,
        sourceId: sourceId ?? null,
      });
      navigate(`/service-technique/arretes/${acte.id}`);
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Création impossible');
    } finally { setSaving(false); }
  }

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Actes" accent="tech" />
      <ModuleHeader accent="tech" icon={Stamp} eyebrow="Registre des actes" title="Nouvel acte" />

      <main id="main-content" className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8 space-y-4">
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}

        {sourceType && SOURCES[sourceType] && (
          <Alert variant="info" icon={Info}>
            Cet acte est rédigé à partir d'<strong>{SOURCES[sourceType]}</strong>.
            Ce que la mairie sait déjà est repris automatiquement — vous n'avez à
            compléter que le reste.
          </Alert>
        )}

        {loading ? (
          <Card variant="glass" padded>
            <p className="flex items-center gap-2 text-sm text-ink-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement…
            </p>
          </Card>
        ) : (
          <Card variant="glass" padded>
            <div className="grid gap-4">
              <div>
                <label className={LABEL} htmlFor="an-modele">Modèle</label>

                {/* ── Filtrer avant de choisir (29/07) ────────────────────────
                    Le catalogue compte ~150 modèles. Arriver d'une délibération
                    et devoir tout dérouler, c'était le retour d'Henrico. */}
                <div className="mb-2 grid gap-2 sm:grid-cols-3">
                  <input
                    aria-label="Rechercher un modèle"
                    placeholder="Rechercher un mot…"
                    value={recherche}
                    onChange={(e) => setRecherche(e.target.value)}
                    className={CHAMP}
                  />
                  <select aria-label="Filtrer par catégorie" value={filtreCategorie}
                    onChange={(e) => setFiltreCategorie(e.target.value)} className={CHAMP}>
                    <option value="">Toutes les catégories</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select aria-label="Filtrer par type d’acte" value={filtreType}
                    onChange={(e) => setFiltreType(e.target.value)} className={CHAMP}>
                    <option value="">Tous les types</option>
                    <option value="individuel">Individuel (se notifie)</option>
                    <option value="reglementaire">Réglementaire (s’affiche)</option>
                  </select>
                </div>

                <select id="an-modele" value={modeleId}
                  onChange={(e) => void choisirModele(e.target.value)} className={CHAMP}>
                  <option value="">— Choisir un modèle —</option>
                  {modelesFiltres.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.categorie ? `${m.categorie} · ` : ''}{m.libelle}
                    </option>
                  ))}
                </select>

                <p className="mt-1 text-2xs text-ink-subtle">
                  {modelesFiltres.length === modeles.length
                    ? `${modeles.length} modèles au catalogue`
                    : `${modelesFiltres.length} sur ${modeles.length} modèles`}
                  {modelesFiltres.length === 0 && modeles.length > 0
                    && ' — aucun ne correspond, élargissez la recherche'}
                </p>
              </div>

              <div>
                <label className={LABEL} htmlFor="an-titre">Titre *</label>
                <input id="an-titre" value={titre}
                  onChange={(e) => setTitre(e.target.value)} className={CHAMP} />
              </div>

              <div>
                <label className={LABEL} htmlFor="an-objet">Objet</label>
                <input id="an-objet" value={objet}
                  onChange={(e) => setObjet(e.target.value)} className={CHAMP} />
              </div>

              {champs.length > 0 && (
                <div className="rounded-r-md border border-border bg-surface/40 p-3">
                  <p className="mb-2 text-sm font-medium text-ink">
                    {resteASaisir === 0
                      ? 'Tout est rempli'
                      : `${resteASaisir} champ${resteASaisir > 1 ? 's' : ''} à compléter`}
                  </p>
                  <p className="mb-3 text-xs text-ink-subtle">
                    Ce qui reste après ce que la commune et la fiche d'origine ont
                    déjà rempli.
                  </p>
                  <ChampsModele champs={champs} valeurs={valeurs}
                    onChange={(cle, v) => setValeurs((p) => ({ ...p, [cle]: v }))} />
                </div>
              )}

              {corps && (
                <div>
                  <label className={LABEL} htmlFor="an-corps">Corps de l'acte</label>
                  <textarea id="an-corps" value={corps} rows={12}
                    onChange={(e) => setCorps(e.target.value)}
                    className="w-full rounded-r-lg border border-border bg-surface/60 p-3 font-mono text-xs text-ink" />
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <Button variant="primary" size="sm" loading={saving}
                  disabled={!titre.trim()} onClick={() => void creer()}>
                  <FileCheck2 className="h-4 w-4" aria-hidden="true" /> Créer le brouillon
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                  Annuler
                </Button>
              </div>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
